import { DomOverlay } from './DomOverlay';
import { Starfield } from './Starfield';
import { Haptics } from '../Haptics';
import { CodexManager, CodexEntry } from '../CodexManager';
import {
  Family,
  Tier,
  FAMILY_COLORS,
  FAMILY_LABELS,
  STANDARD_FAMILIES,
  TIER_NAMES,
} from '../../config';

type CodexEvents = {
  back: void;
};

type ViewMode = 'family' | 'matrix';

/**
 * CodexOverlay — DOM-based elemental codex mirroring
 * `design/handoff/source/codex.jsx`.
 *
 * Two view modes:
 *   - Family (default): horizontal family rail → tier-card list for the
 *     active family.
 *   - Matrix: 8×7 grid across all families + tiers at a glance.
 *
 * Undiscovered entries render as ??? / silhouette glyphs at reduced opacity;
 * discovered entries show name + tier + lore.
 */
export class CodexOverlay extends DomOverlay<CodexEvents> {
  private codex: CodexManager;
  private viewMode: ViewMode = 'family';
  private selectedFamily: Family = Family.Fire;

  constructor(codex: CodexManager) {
    super();
    this.codex = codex;
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.classList.add('codex-root');
    new Starfield(this.rootEl).mount();
    this.rootEl.appendChild(this.buildContent());
    this.injectStyles();
  }

  private buildContent(): HTMLDivElement {
    const wrap = this.el('div', 'codex-wrap');
    wrap.appendChild(this.buildTopBar());
    wrap.appendChild(this.buildViewToggle());
    wrap.appendChild(this.buildBody());
    return wrap;
  }

  private buildTopBar(): HTMLDivElement {
    const bar = this.el('div', 'codex-topbar');

    const back = this.el('button', 'codex-iconbtn', '‹');
    back.setAttribute('aria-label', 'Back');
    back.addEventListener('click', () => {
      Haptics.light();
      this.emit('back', undefined);
    });

    const title = this.el('div', 'codex-title');
    title.appendChild(this.el('div', 'codex-kicker type-micro', 'CODEX'));
    title.appendChild(this.el('h1', 'codex-h1', 'Elemental Archive'));

    const stats = this.codex.getTotalStats();
    const ring = this.buildProgressRing(stats.percent);

    bar.appendChild(back);
    bar.appendChild(title);
    bar.appendChild(ring);

    const meta = this.el('div', 'codex-meta');
    meta.appendChild(this.el('span', 'codex-meta__count', `${stats.discovered}/${stats.total}`));
    meta.appendChild(this.el('span', 'codex-meta__label type-micro', 'DISCOVERED'));

    const header = this.el('div', 'codex-header');
    header.appendChild(bar);
    header.appendChild(meta);
    return header;
  }

  private buildProgressRing(percent: number): HTMLElement {
    const size = 52;
    const stroke = 4;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - percent / 100);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svg.setAttribute('class', 'codex-ring');

    const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    track.setAttribute('cx', String(size / 2));
    track.setAttribute('cy', String(size / 2));
    track.setAttribute('r', String(r));
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke', 'rgba(73,86,112,0.25)');
    track.setAttribute('stroke-width', String(stroke));
    svg.appendChild(track);

    const meter = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    meter.setAttribute('cx', String(size / 2));
    meter.setAttribute('cy', String(size / 2));
    meter.setAttribute('r', String(r));
    meter.setAttribute('fill', 'none');
    meter.setAttribute('stroke', 'var(--accent-primary)');
    meter.setAttribute('stroke-width', String(stroke));
    meter.setAttribute('stroke-linecap', 'round');
    meter.setAttribute('stroke-dasharray', String(c));
    meter.setAttribute('stroke-dashoffset', String(offset));
    meter.setAttribute(
      'style',
      `transform: rotate(-90deg); transform-origin: ${size / 2}px ${size / 2}px;`
    );
    svg.appendChild(meter);

    return svg as unknown as HTMLElement;
  }

  private buildViewToggle(): HTMLDivElement {
    const toggle = this.el('div', 'codex-toggle');
    const modes: Array<{ id: ViewMode; label: string }> = [
      { id: 'family', label: 'Family' },
      { id: 'matrix', label: 'Matrix' },
    ];
    for (const m of modes) {
      const btn = this.el('button', 'codex-toggle__btn', m.label);
      if (m.id === this.viewMode) btn.classList.add('is-active');
      btn.addEventListener('click', () => {
        if (this.viewMode === m.id) return;
        Haptics.light();
        this.viewMode = m.id;
        this.render();
      });
      toggle.appendChild(btn);
    }
    return toggle;
  }

  private buildBody(): HTMLDivElement {
    const body = this.el('div', 'codex-body');
    if (this.viewMode === 'family') {
      body.appendChild(this.buildFamilyRail());
      body.appendChild(this.buildFamilyPanel());
      body.appendChild(this.buildTierCards());
    } else {
      body.appendChild(this.buildMatrix());
    }
    return body;
  }

  private buildFamilyRail(): HTMLDivElement {
    const rail = this.el('div', 'codex-rail');
    for (const family of STANDARD_FAMILIES) {
      const entries = this.codex.getByFamily(family);
      const discovered = entries.filter((e) => e.discovered).length;
      const color = FAMILY_COLORS[family];
      const hex = `#${color.toString(16).padStart(6, '0')}`;

      const pill = this.el('button', 'codex-pill');
      if (family === this.selectedFamily) pill.classList.add('is-active');
      pill.style.setProperty('--family-color', hex);

      pill.appendChild(this.el('div', 'codex-pill__dot'));
      pill.appendChild(this.el('div', 'codex-pill__label', FAMILY_LABELS[family]));
      pill.appendChild(this.el('div', 'codex-pill__count', `${discovered}/${entries.length}`));

      const fillBar = this.el('div', 'codex-pill__fill');
      const fill = this.el('div', 'codex-pill__fill-inner');
      fill.style.width = `${(discovered / entries.length) * 100}%`;
      fillBar.appendChild(fill);
      pill.appendChild(fillBar);

      pill.addEventListener('click', () => {
        if (this.selectedFamily === family) return;
        Haptics.light();
        this.selectedFamily = family;
        this.render();
      });
      rail.appendChild(pill);
    }
    return rail;
  }

  private buildFamilyPanel(): HTMLDivElement {
    const entries = this.codex.getByFamily(this.selectedFamily);
    const discovered = entries.filter((e) => e.discovered).length;
    const color = FAMILY_COLORS[this.selectedFamily];
    const hex = `#${color.toString(16).padStart(6, '0')}`;

    const panel = this.el('div', 'codex-famhead glass-panel');
    panel.style.setProperty('--family-color', hex);

    const left = this.el('div', 'codex-famhead__left');
    left.appendChild(this.el('div', 'codex-famhead__kicker type-micro', `FAMILY · ${this.selectedFamily.toUpperCase()}`));
    left.appendChild(this.el('h2', 'codex-famhead__name', FAMILY_LABELS[this.selectedFamily]));
    left.appendChild(
      this.el(
        'div',
        'codex-famhead__progress',
        `${discovered} of ${entries.length} found`
      )
    );

    const right = this.el('div', 'codex-famhead__right');
    right.appendChild(this.buildSilhouette(this.selectedFamily));

    panel.appendChild(left);
    panel.appendChild(right);
    return panel;
  }

  private buildSilhouette(family: Family): HTMLElement {
    const color = FAMILY_COLORS[family];
    const hex = `#${color.toString(16).padStart(6, '0')}`;
    const wrap = this.el('div', 'codex-silhouette');
    wrap.style.setProperty('--family-color', hex);
    wrap.textContent = FAMILY_LABELS[family].charAt(0);
    return wrap;
  }

  private buildTierCards(): HTMLDivElement {
    const list = this.el('div', 'codex-cards');
    const entries = this.codex.getByFamily(this.selectedFamily);
    const color = FAMILY_COLORS[this.selectedFamily];
    const hex = `#${color.toString(16).padStart(6, '0')}`;

    for (const entry of entries) {
      list.appendChild(this.buildTierCard(entry, hex));
    }
    return list;
  }

  private buildTierCard(entry: CodexEntry, familyHex: string): HTMLDivElement {
    const card = this.el('div', 'codex-card');
    if (!entry.discovered) card.classList.add('is-locked');
    card.style.setProperty('--family-color', familyHex);

    const glyph = this.el('div', 'codex-card__glyph');
    glyph.textContent = entry.discovered ? entry.name.charAt(0) : '?';
    card.appendChild(glyph);

    const body = this.el('div', 'codex-card__body');
    const head = this.el('div', 'codex-card__head');
    head.appendChild(
      this.el('span', 'codex-card__tier type-micro', `T${entry.tier} · ${TIER_NAMES[entry.tier]}`)
    );
    head.appendChild(
      this.el('span', 'codex-card__name', entry.discovered ? entry.name : '???')
    );
    body.appendChild(head);
    body.appendChild(
      this.el(
        'p',
        'codex-card__lore',
        entry.discovered ? entry.lore : '?????'
      )
    );
    card.appendChild(body);

    return card;
  }

  private buildMatrix(): HTMLDivElement {
    const matrix = this.el('div', 'codex-matrix');

    const header = this.el('div', 'codex-matrix__header');
    header.appendChild(this.el('div', 'codex-matrix__corner'));
    for (let t = Tier.Fragment; t <= Tier.Eternal; t++) {
      const h = this.el('div', 'codex-matrix__th type-micro', `T${t}`);
      header.appendChild(h);
    }
    matrix.appendChild(header);

    for (const family of STANDARD_FAMILIES) {
      const row = this.el('div', 'codex-matrix__row');
      const color = FAMILY_COLORS[family];
      const hex = `#${color.toString(16).padStart(6, '0')}`;
      row.style.setProperty('--family-color', hex);

      const label = this.el('div', 'codex-matrix__rowlabel');
      label.textContent = FAMILY_LABELS[family];
      row.appendChild(label);

      for (let t = Tier.Fragment; t <= Tier.Eternal; t++) {
        const entries = this.codex.getByFamily(family);
        const entry = entries.find((e) => e.tier === t);
        const cell = this.el('div', 'codex-matrix__cell');
        if (!entry || !entry.discovered) cell.classList.add('is-locked');
        const glyph = this.el('div', 'codex-matrix__glyph');
        glyph.textContent = entry?.discovered ? entry.name.charAt(0) : '?';
        cell.appendChild(glyph);
        row.appendChild(cell);
      }
      matrix.appendChild(row);
    }
    return matrix;
  }

  private injectStyles(): void {
    if (document.getElementById('codex-overlay-styles')) return;
    const tag = document.createElement('style');
    tag.id = 'codex-overlay-styles';
    tag.textContent = CODEX_STYLES;
    document.head.appendChild(tag);
  }
}

const CODEX_STYLES = `
.codex-root {
  position: absolute; inset: 0;
  background: var(--bg-void);
  overflow: hidden;
}
.codex-wrap {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; gap: 14px;
  padding: calc(env(safe-area-inset-top, 0) + 18px) 18px
          calc(env(safe-area-inset-bottom, 0) + 18px);
  height: 100%;
  overflow-y: auto;
}
.codex-header {
  display: flex; flex-direction: column; gap: 6px;
}
.codex-topbar {
  display: flex; align-items: center; gap: 14px;
}
.codex-iconbtn {
  width: 40px; height: 40px; min-width: 40px;
  border-radius: var(--r-lg, 10px);
  background: rgba(21, 24, 33, 0.6);
  border: 1px solid rgba(73, 86, 112, 0.3);
  color: var(--text-secondary);
  font-size: 22px; line-height: 1;
  cursor: pointer; padding: 0;
  display: flex; align-items: center; justify-content: center;
  transition: color 0.2s, border-color 0.2s;
}
.codex-iconbtn:hover, .codex-iconbtn:focus-visible {
  color: var(--accent-primary);
  border-color: rgba(var(--accent-primary-rgb), 0.5);
  outline: none;
}
.codex-title { flex: 1; min-width: 0; }
.codex-kicker {
  color: var(--accent-primary);
  letter-spacing: 0.22em;
}
.codex-h1 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 22px; font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.02em;
  margin: 2px 0 0;
  text-shadow: 0 0 20px rgba(var(--accent-primary-rgb), 0.3);
}
.codex-ring { flex-shrink: 0; }
.codex-meta {
  display: flex; align-items: baseline; gap: 10px;
  padding-left: 54px;
}
.codex-meta__count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px; font-weight: 600;
  color: var(--text-primary);
}
.codex-meta__label {
  color: var(--text-secondary);
  letter-spacing: 0.18em;
}

.codex-toggle {
  display: flex; gap: 6px;
  padding: 4px;
  background: rgba(13, 14, 20, 0.75);
  border: 1px solid rgba(73, 86, 112, 0.25);
  border-radius: var(--r-xl, 12px);
  align-self: flex-start;
}
.codex-toggle__btn {
  padding: 8px 18px; min-height: 36px;
  border-radius: var(--r-md, 8px);
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}
.codex-toggle__btn.is-active {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-primary-deep));
  color: #050507;
  box-shadow: 0 0 14px -3px var(--accent-primary);
}

.codex-body {
  display: flex; flex-direction: column; gap: 14px;
}

.codex-rail {
  display: flex; gap: 8px;
  overflow-x: auto;
  padding: 2px;
  scrollbar-width: none;
}
.codex-rail::-webkit-scrollbar { display: none; }
.codex-pill {
  flex: 0 0 auto;
  min-width: 108px;
  padding: 10px 12px 8px;
  border-radius: var(--r-xl, 12px);
  background: rgba(13, 14, 20, 0.7);
  border: 1px solid rgba(73, 86, 112, 0.25);
  color: var(--text-secondary);
  font-family: inherit;
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.codex-pill.is-active {
  border-color: color-mix(in srgb, var(--family-color, var(--accent-primary)) 60%, transparent);
  color: var(--text-primary);
  box-shadow:
    0 0 18px -4px var(--family-color, var(--accent-primary)),
    0 0 0 1px rgba(var(--accent-primary-rgb), 0.1) inset;
}
.codex-pill__dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--family-color, var(--accent-primary));
  box-shadow: 0 0 8px var(--family-color, var(--accent-primary));
}
.codex-pill__label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 12px; font-weight: 600;
  letter-spacing: 0.08em;
}
.codex-pill__count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--text-secondary);
}
.codex-pill__fill {
  width: 100%; height: 3px;
  background: rgba(73, 86, 112, 0.25);
  border-radius: 2px;
  overflow: hidden;
}
.codex-pill__fill-inner {
  height: 100%;
  background: var(--family-color, var(--accent-primary));
  transition: width 0.4s var(--ease-spring);
}

.codex-famhead {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px;
  border-radius: var(--r-2xl, 14px);
  background: rgba(13, 14, 20, 0.82);
  border: 1px solid color-mix(in srgb, var(--family-color) 30%, transparent);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.5),
    0 0 40px -12px color-mix(in srgb, var(--family-color) 60%, transparent);
}
.codex-famhead__left { flex: 1; min-width: 0; }
.codex-famhead__kicker {
  color: var(--family-color);
  letter-spacing: 0.2em;
  margin-bottom: 4px;
}
.codex-famhead__name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 22px; font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.codex-famhead__progress {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--text-secondary);
}
.codex-famhead__right { flex: 0 0 auto; }
.codex-silhouette {
  width: 64px; height: 64px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    color-mix(in srgb, var(--family-color) 28%, transparent) 0%,
    transparent 70%
  );
  border: 1px solid color-mix(in srgb, var(--family-color) 40%, transparent);
  color: var(--family-color);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 28px; font-weight: 700;
  text-shadow: 0 0 12px var(--family-color);
}

.codex-cards {
  display: flex; flex-direction: column; gap: 8px;
}
.codex-card {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  border-radius: var(--r-xl, 12px);
  background: rgba(13, 14, 20, 0.7);
  border: 1px solid rgba(73, 86, 112, 0.2);
  border-left: 3px solid var(--family-color);
}
.codex-card.is-locked {
  opacity: 0.55;
  border-left-color: rgba(73, 86, 112, 0.4);
}
.codex-card__glyph {
  width: 40px; height: 40px; min-width: 40px;
  border-radius: var(--r-md, 8px);
  background: color-mix(in srgb, var(--family-color) 18%, transparent);
  color: var(--family-color);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px; font-weight: 700;
}
.codex-card.is-locked .codex-card__glyph {
  background: rgba(21, 24, 33, 0.55);
  color: var(--text-tertiary);
}
.codex-card__body { flex: 1; min-width: 0; }
.codex-card__head {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  margin-bottom: 2px;
}
.codex-card__tier {
  color: var(--family-color);
  letter-spacing: 0.16em;
}
.codex-card.is-locked .codex-card__tier {
  color: var(--text-tertiary);
}
.codex-card__name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px; font-weight: 600;
  color: var(--text-primary);
}
.codex-card.is-locked .codex-card__name {
  color: var(--text-tertiary);
}
.codex-card__lore {
  font-size: 11px; line-height: 1.4;
  color: var(--text-warm, var(--text-secondary));
  font-style: italic;
  margin: 0;
}
.codex-card.is-locked .codex-card__lore {
  color: var(--text-tertiary);
  font-style: normal;
}

.codex-matrix {
  display: flex; flex-direction: column; gap: 4px;
  padding: 10px;
  border-radius: var(--r-2xl, 14px);
  background: rgba(13, 14, 20, 0.7);
  border: 1px solid rgba(73, 86, 112, 0.2);
  overflow-x: auto;
}
.codex-matrix__header {
  display: grid;
  grid-template-columns: 72px repeat(7, 1fr);
  gap: 4px;
}
.codex-matrix__corner {}
.codex-matrix__th {
  color: var(--text-secondary);
  letter-spacing: 0.12em;
  text-align: center;
  padding: 6px 0;
}
.codex-matrix__row {
  display: grid;
  grid-template-columns: 72px repeat(7, 1fr);
  gap: 4px;
}
.codex-matrix__rowlabel {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px; font-weight: 600;
  color: var(--family-color);
  display: flex; align-items: center;
  padding-right: 4px;
  letter-spacing: 0.06em;
}
.codex-matrix__cell {
  aspect-ratio: 1;
  border-radius: var(--r-sm, 6px);
  background: color-mix(in srgb, var(--family-color) 14%, transparent);
  border: 1px solid color-mix(in srgb, var(--family-color) 24%, transparent);
  display: flex; align-items: center; justify-content: center;
}
.codex-matrix__cell.is-locked {
  background: rgba(21, 24, 33, 0.4);
  border-color: rgba(73, 86, 112, 0.2);
  opacity: 0.35;
}
.codex-matrix__glyph {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px; font-weight: 700;
  color: var(--family-color);
}
.codex-matrix__cell.is-locked .codex-matrix__glyph {
  color: var(--text-tertiary);
}
`;
